
import { Routes, Route, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { useCanonical } from "./hooks/useCanonical";
import ConfirmationRedirect from './components/auth/ConfirmationRedirect';

// Use React.lazy for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Signup = lazy(() => import("./pages/Signup"));
const Index = lazy(() => import("./pages/Index"));
const Profile = lazy(() => import("./pages/Profile"));
const Offers = lazy(() => import("./pages/Offers"));
const Partners = lazy(() => import("./pages/Partners"));
const ManageOffers = lazy(() => import("./pages/ManageOffers"));
const ManageCompany = lazy(() => import("./pages/ManageCompany"));
const Reports = lazy(() => import("./pages/Reports"));
const MyPackage = lazy(() => import("./pages/MyPackage"));
const AdminPartnerUsage = lazy(() => import("./pages/AdminPartnerUsage"));
const AdminCommunities = lazy(() => import("./pages/AdminCommunities"));
const Communities = lazy(() => import("./pages/Communities"));
const ExpertTopics = lazy(() => import("./pages/ExpertTopics"));
const ManageTopics = lazy(() => import("./pages/ManageTopics"));
const LocationsMap = lazy(() => import("./pages/LocationsMap"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-eo-blue"></div>
  </div>
);

export const AppRoutes = () => {
  // Use the canonical hook to update the canonical link
  useCanonical();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <Index />
            </RequireAuth>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <Profile />
            </RequireAuth>
          }
        />
        <Route
          path="/offers"
          element={
            <RequireAuth>
              <Offers />
            </RequireAuth>
          }
        />
        <Route
          path="/expert-topics"
          element={
            <RequireAuth>
              <ExpertTopics />
            </RequireAuth>
          }
        />
        <Route
          path="/locations-map"
          element={
            <RequireAuth>
              <LocationsMap />
            </RequireAuth>
          }
        />
        <Route
          path="/manage-topics"
          element={
            <RequireAuth requiredRoles={['admin', 'partner']}>
              <ManageTopics />
            </RequireAuth>
          }
        />
        <Route
          path="/manage-offers"
          element={
            <RequireAuth requiredRoles={['admin', 'partner']}>
              <ManageOffers />
            </RequireAuth>
          }
        />
        <Route
          path="/manage-company"
          element={
            <RequireAuth requiredRoles={['admin', 'partner', 'member-showcase']}>
              <ManageCompany />
            </RequireAuth>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAuth requiredRoles={['admin', 'partner']}>
              <Reports />
            </RequireAuth>
          }
        />
        <Route
          path="/my-package"
          element={
            <RequireAuth requiredRoles={['admin', 'partner']}>
              <MyPackage />
            </RequireAuth>
          }
        />
        <Route
          path="/communities"
          element={
            <RequireAuth requiredRoles={['member', 'admin']}>
              <Communities />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/communities"
          element={
            <RequireAuth requiredRoles={['admin']}>
              <AdminCommunities />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partner-usage"
          element={
            <RequireAuth requiredRoles={['admin']}>
              <AdminPartnerUsage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAuth requiredRoles={['admin']}>
              <AdminUsers />
            </RequireAuth>
          }
        />
        <Route
          path="/partners"
          element={
            <RequireAuth>
              <Partners />
            </RequireAuth>
          }
        />
        <Route path="/auth-confirmation" element={<ConfirmationRedirect />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
